/**
 * The reading pane's sentence for a message's `renderStatus` (#42; ADR 0017,
 * amended). The server says when the sender's HTML was not shown, a body
 * could not be read, or a part came back missing; this says so in a sentence
 * the reader can act on.
 *
 * Deliberately not shared with the attached-email viewer: its sentences are
 * about an attachment ("This attachment has no email headers…"), and it
 * builds its own detail without `renderStatus`, so the notice never appears
 * twice.
 */

import type { MessageDetail } from '@/api';

/**
 * The notice for a message, or `null` when there is nothing to say: `ok`, an
 * absent status (an older server, or a body from the offline cache), and
 * `degraded` on a truncated message, whose "cut short" sentence already says
 * it — the only other cause of `degraded` is a sized part that came back
 * empty, and that is the sentence below.
 */
export function renderStatusNotice(
  message: Pick<MessageDetail, 'renderStatus' | 'truncated'>,
): string | null {
  switch (message.renderStatus) {
    case 'text_only':
      return 'This message’s formatting couldn’t be shown safely, so it is shown as plain text.';
    case 'unreadable':
      return 'This message’s text couldn’t be read. View source shows it as it arrived.';
    case 'degraded':
      return message.truncated === true
        ? null
        : 'Part of this message was missing or damaged, so it may be incomplete.';
    default:
      return null;
  }
}
